import { useState, useEffect, useRef } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Phone, PhoneCall, X, AlertCircle, Loader2, PhoneOff, Copy, Check, Keyboard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useIsMobile } from "@/hooks/use-mobile";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { isConsentState } from "@/lib/dialer-consent-states";

// Wave 3 — Browser dialer with @twilio/voice-sdk wiring.
// When the workspace has Twilio credentials configured, we mint an
// AccessToken via /api/dialer/token, instantiate a Twilio Device, and
// place the outbound call directly from the browser. When credentials
// are missing OR the SDK fails to load, we fall back to the manual
// log-after-the-fact UX so the rep can capture notes regardless.

interface MiniDialerProps {
  phone: string;
  contactName?: string | null;
  leadId?: string | null;
  clientId?: string | null;
  onClose: () => void;
}

type CallState = "idle" | "connecting" | "ringing" | "in-progress" | "completed" | "error";

// Twilio Device errors that mean "the access token is stale", not "something
// is broken". These are recoverable by minting a fresh token in place.
//   20104 — access token expired
//   31204 — invalid access token signature
//   31205 — access token expired (JWT layer)
const EXPIRED_TOKEN_CODES = new Set([20104, 31204, 31205]);

// If a token error re-fires this soon after a successful renewal, renewing
// isn't the fix — stop retrying and tell the rep to reload.
const RECOVERY_COOLDOWN_MS = 30_000;

// Call states where a Twilio call object is live. Token trouble must not
// rewrite the UI out from under these.
const LIVE_CALL_STATES = new Set<CallState>(["connecting", "ringing", "in-progress"]);

export function MiniDialer({ phone, contactName, leadId, clientId, onClose }: MiniDialerProps) {
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [notes, setNotes] = useState("");
  const [duration, setDuration] = useState(0);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [callState, setCallState] = useState<CallState>("idle");
  const [copied, setCopied] = useState(false);
  const [activeSid, setActiveSid] = useState<string | null>(null);

  const deviceRef = useRef<any>(null);
  const callRef = useRef<any>(null);
  // The token currently installed on the live Device, so a refreshed token can
  // be applied in place instead of rebuilding (and dropping an active call).
  const lastAppliedTokenRef = useRef<string | null>(null);
  // Single-flight guard + epoch//timestamp bookkeeping for token renewal, so a
  // Device that keeps rejecting its token can't spin up endless refetches.
  const deviceEpochRef = useRef(0);
  const startedAtRef = useRef<number | null>(null);
  const tickRef = useRef<number | null>(null);
  const notesRef = useRef<HTMLTextAreaElement>(null);
  // Tracks whether the call was answered vs cancelled/no-answer so "Log call"
  // can persist the correct status (the old silent persistCall did this).
  const [callOutcomeStatus, setCallOutcomeStatus] = useState<"completed" | "no-answer">("completed");
  // sdkReady MUST be state (not computed from deviceRef) so that setting
  // deviceRef.current inside a useEffect triggers a re-render and transitions
  // the UI out of "Connecting to dialer…". Refs don't cause re-renders.
  const [sdkReady, setSdkReady] = useState(false);
  // DTMF keypad state — only active during in-progress browser calls
  const [showKeypad, setShowKeypad] = useState(false);
  const [dialedDigits, setDialedDigits] = useState("");

  const tokenProbe = useQuery<{ token?: string; identity?: string; error?: string }>({
    queryKey: ["/api/dialer/token-probe"],
    queryFn: async () => {
      try {
        const res = await apiRequest("POST", "/api/dialer/token", {});
        return res.json();
      } catch (e: any) {
        const msg = e?.message || "Dialer not configured";
        return { error: msg };
      }
    },
    retry: false,
    staleTime: 60_000,
  });

  const { data: dialerSettings } = useQuery<{ effectiveRecordingMode: string; autoFocusLog?: boolean; routingPreference?: string }>({
    queryKey: ["/api/settings/dialer"],
    staleTime: 60_000,
  });

  // When the rep has opted into phone routing we never load/use the browser
  // SDK leg — Twilio rings their phone first, then bridges to the lead. The
  // call-history logging path is shared (see logCall below). (Task #577)
  const phoneRouting = dialerSettings?.routingPreference === "phone";

  // Show the two-party-consent banner when the destination area code maps
  // to one of the 11 consent states AND recording is active.
  const effectiveRecordingMode = dialerSettings?.effectiveRecordingMode || "off";
  const showConsentBanner =
    effectiveRecordingMode !== "off" && phone && isConsentState(phone);

  // autoFocusLog defaults to true when the setting is absent (new users,
  // or profile row predates this column).
  const autoFocusLog = dialerSettings?.autoFocusLog !== false;

  useEffect(() => {
    if (tokenProbe.data?.error) setTokenError(tokenProbe.data.error);
  }, [tokenProbe.data]);

  // Auto-focus the notes textarea when a browser call completes and the
  // user has not opted out of the behaviour.
  useEffect(() => {
    if (callState === "completed" && autoFocusLog) {
      // Small defer so the re-render that hides the hang-up button and
      // shows the "Log call" button completes before we steal focus.
      const t = setTimeout(() => {
        notesRef.current?.focus();
      }, 50);
      return () => clearTimeout(t);
    }
  }, [callState, autoFocusLog]);

  // Initialize Twilio Device when token arrives. The SDK is dynamically
  // imported so that workspaces without dialer enabled never load it.
  // Keep a stable handle on the refetcher so the long-lived Device listeners
  // below never close over a stale query object.
  const refetchTokenRef = useRef(tokenProbe.refetch);
  refetchTokenRef.current = tokenProbe.refetch;

  useEffect(() => {
    let cancelled = false;
    if (phoneRouting) return; // phone-routed reps never use the browser SDK leg
    const token = tokenProbe.data?.token;
    if (!token) return;

    // A refreshed token must NOT tear the Device down — that would drop a call
    // in progress. Only the first token builds a Device; later ones are
    // applied in place.
    if (deviceRef.current) {
      if (lastAppliedTokenRef.current !== token) {
        try {
          deviceRef.current.updateToken?.(token);
          lastAppliedTokenRef.current = token;
        } catch {
          // non-fatal — the tokenWillExpire path will try again
        }
      }
      return;
    }

    (async () => {
      try {
        const sdk: any = await import("@twilio/voice-sdk");
        if (cancelled) return;
        const Device = sdk?.Device || sdk?.default?.Device;
        if (!Device) {
          setTokenError("Twilio Voice SDK shape unexpected");
          return;
        }
        const device = new Device(token, { codecPreferences: ["opus", "pcmu"] });
        deviceRef.current = device;
        lastAppliedTokenRef.current = token;
        const epoch = ++deviceEpochRef.current;
        // Events can arrive after this Device has been replaced or destroyed.
        const isCurrent = () => deviceRef.current === device && deviceEpochRef.current === epoch;

        // ── Token renewal, scoped to THIS Device ──────────────────────────
        // Access tokens carry a 1-hour TTL (see mintAccessToken). Without
        // renewal a rep who leaves the dialer open past the hour finds it dead
        // with a generic "unavailable" until they hard-refresh. The inbound
        // listener has always renewed; this one did not.
        //
        // All the guard state lives in this closure rather than in refs, so it
        // is inherently per-Device: a retiring Device can neither block nor
        // satisfy its successor's renewal.
        let renewInFlight = false;
        let lastAttemptAt: number | null = null;
        let renewalDead = false; // latched: only a fresh Device clears it

        type RenewOutcome = "ok" | "busy" | "throttled" | "failed";
        const renewToken = async (): Promise<RenewOutcome> => {
          if (renewalDead) return "failed";
          if (renewInFlight) return "busy";
          // Timestamp the ATTEMPT, not the success — otherwise a failing
          // renewal never advances the clock and every subsequent error
          // starts another request.
          if (lastAttemptAt != null && Date.now() - lastAttemptAt < RECOVERY_COOLDOWN_MS) {
            return "throttled";
          }
          renewInFlight = true;
          lastAttemptAt = Date.now();
          try {
            const res: any = await refetchTokenRef.current?.();
            const next = res?.data?.token;
            if (!isCurrent()) return "failed";
            if (!next) {
              renewalDead = true;
              return "failed";
            }
            device.updateToken?.(next);
            lastAppliedTokenRef.current = next;
            return "ok";
          } catch {
            renewalDead = true;
            return "failed";
          } finally {
            renewInFlight = false;
          }
        };

        // Renewal is over: stop trying and tell the rep. Reopening the dialer
        // builds a fresh Device, which clears the latch.
        const failRenewalTerminally = (code?: unknown) => {
          renewalDead = true;
          setTokenError(
            `Your dialer session expired and could not be renewed. Refresh the page.${code ? ` (code ${code})` : ""}`,
          );
          setSdkReady(false);
          setCallState((prev) => (LIVE_CALL_STATES.has(prev) ? prev : "error"));
        };

        // Fires ~3 min before the JWT lapses.
        device.on?.("tokenWillExpire", () => {
          if (!isCurrent()) return;
          void (async () => {
            const outcome = await renewToken();
            if (!isCurrent() || outcome === "ok" || outcome === "busy") return;
            // A proactive renewal that failed must surface now — otherwise the
            // dialer keeps advertising itself as ready until the token lapses
            // and the rep discovers it mid-call-attempt.
            failRenewalTerminally();
          })();
        });

        device.on?.("error", (err: any) => {
          if (!isCurrent()) return;
          const code = err?.code;
          // Always leave a trace in the browser console — the on-screen text
          // is easy to miss and support has no other view into this.
          console.error("[MiniDialer] Twilio Device error", { code, message: err?.message });

          // An expired/invalid token is recoverable without user action: mint
          // a new one and carry on rather than showing a dead-end warning.
          if (EXPIRED_TOKEN_CODES.has(code)) {
            void (async () => {
              const outcome = await renewToken();
              if (!isCurrent()) return;
              // Another renewal for this same Device is already running; let
              // that one settle the UI instead of racing it.
              if (outcome === "busy") return;
              if (outcome === "ok") {
                setTokenError(null);
                setSdkReady(true);
                // Never yank a live call's UI out from under the rep — an
                // expired token does not drop a call already in progress.
                setCallState((prev) => (prev === "error" ? "idle" : prev));
                return;
              }
              // "throttled" means the error re-fired right after a renewal, so
              // renewing plainly isn't the fix; "failed" is already latched.
              // Latch either way — otherwise the cooldown merely rate-limits
              // an endless renew/error cycle instead of ending it.
              failRenewalTerminally(code);
            })();
            return;
          }

          if (code === 31401 || code === 31402) {
            setTokenError("Please allow microphone access for clarik.io in your browser settings, then refresh the page.");
          } else {
            setTokenError(`${err?.message || "Dialer error"}${code ? ` (code ${code})` : ""}`);
          }
          setSdkReady(false);
          setCallState("error");
        });
        setSdkReady(true);
      } catch (e: any) {
        console.error("[MiniDialer] Failed to load voice SDK", e);
        setTokenError(e?.message || "Failed to load voice SDK");
        setSdkReady(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tokenProbe.data?.token, phoneRouting]);

  // Switching to phone routing retires the browser Device.
  useEffect(() => {
    if (!phoneRouting) return;
    try { deviceRef.current?.destroy?.(); } catch {}
    deviceRef.current = null;
    lastAppliedTokenRef.current = null;
    setSdkReady(false);
  }, [phoneRouting]);

  // The Device now outlives token changes, so unmount is the only teardown.
  useEffect(() => {
    return () => {
      try { deviceRef.current?.destroy?.(); } catch {}
      deviceRef.current = null;
      lastAppliedTokenRef.current = null;
    };
  }, []);

  // Tick the on-call duration meter every second while a call is active.
  useEffect(() => {
    if (callState === "in-progress" && startedAtRef.current) {
      tickRef.current = window.setInterval(() => {
        const elapsed = Math.floor((Date.now() - (startedAtRef.current || Date.now())) / 1000);
        setDuration(elapsed);
      }, 1000) as unknown as number;
      return () => {
        if (tickRef.current != null) {
          clearInterval(tickRef.current);
          tickRef.current = null;
        }
      };
    }
  }, [callState]);

  // Phone-routed outbound: Twilio rings the rep's phone first (REST originate),
  // then bridges to the lead via /api/dialer/bridge-voice. There is no browser
  // call object to subscribe to, so we move straight to a "ringing your phone"
  // state and let the rep capture the outcome via "Log call". (Task #577)
  const startPhoneBridge = async () => {
    setCallState("connecting");
    try {
      const res = await apiRequest("POST", "/api/dialer/bridge-call", { to: phone });
      const data = await res.json();
      startedAtRef.current = Date.now();
      try { setActiveSid(data?.sid || null); } catch {}
      setCallState("ringing");
    } catch (e: any) {
      setTokenError(e?.message || "Failed to ring your phone");
      setCallState("error");
    }
  };

  const startCall = async () => {
    if (phoneRouting) {
      return startPhoneBridge();
    }
    if (!deviceRef.current) {
      toast({ title: "Dialer not ready", description: "Browser dialer hasn't connected yet.", variant: "destructive" });
      return;
    }
    setTokenError(null); // clear any stale error from a previous failed attempt
    setCallState("connecting");
    try {
      const params = { To: phone };
      const call = await deviceRef.current.connect({ params });
      callRef.current = call;
      startedAtRef.current = Date.now();
      call.on?.("ringing", () => setCallState("ringing"));
      call.on?.("accept", () => {
        setCallState("in-progress");
        try { setActiveSid(call.parameters?.CallSid || null); } catch {}
      });
      call.on?.("disconnect", () => {
        const elapsed = startedAtRef.current ? Math.floor((Date.now() - startedAtRef.current) / 1000) : 0;
        setDuration(elapsed);
        setCallOutcomeStatus("completed");
        setCallState("completed");
        setShowKeypad(false);
        setDialedDigits("");
        // No silent auto-save — rep uses "Log call" to commit the record
        // so they have time to add notes first.
      });
      call.on?.("cancel", () => {
        setCallOutcomeStatus("no-answer");
        setCallState("completed");
        setShowKeypad(false);
        setDialedDigits("");
        // No silent auto-save — rep may still want to log notes.
      });
      call.on?.("error", (err: any) => {
        const code = err?.code;
        if (code === 31401 || code === 31402) {
          setTokenError("Please allow microphone access for clarik.io in your browser settings, then refresh the page.");
        } else {
          setTokenError(err?.message || "Call error");
        }
        setCallState("error");
      });
    } catch (e: any) {
      setTokenError(e?.message || "Failed to start call");
      setCallState("error");
    }
  };

  const hangup = () => {
    try { callRef.current?.disconnect?.(); } catch {}
  };

  const sendDtmf = (digit: string) => {
    try {
      callRef.current?.sendDigits?.(digit);
      setDialedDigits(prev => (prev + digit).slice(-10));
    } catch (e) {
      console.warn("[Dialer] DTMF send failed:", e);
    }
  };

  const logCall = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/dialer/calls", {
        direction: "outbound",
        toNumber: phone,
        status: callOutcomeStatus,
        durationSeconds: duration,
        leadId: leadId || undefined,
        clientId: clientId || undefined,
        notes: notes || undefined,
        twilioCallSid: activeSid || undefined,
        startedAt: startedAtRef.current
          ? new Date(startedAtRef.current).toISOString()
          : new Date(Date.now() - duration * 1000).toISOString(),
        endedAt: new Date().toISOString(),
      });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Call logged", description: `Saved to call history for ${contactName || phone}` });
      queryClient.invalidateQueries({ queryKey: ["/api/dialer/calls"] });
      onClose();
    },
    onError: (e: any) => {
      toast({ title: "Could not log call", description: String(e?.message || e), variant: "destructive" });
    },
  });

  const inCall = callState === "connecting" || callState === "ringing" || callState === "in-progress";

  return (
    <div
      className="fixed bottom-4 right-4 w-80 bg-card border border-border rounded-lg shadow-xl p-4 z-50 space-y-3"
      data-testid="mini-dialer"
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="font-semibold flex items-center gap-2">
            <PhoneCall className="w-4 h-4 text-primary" />
            Call {contactName || phone}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">{phone}</div>
          {callState !== "idle" && (
            <div className="text-xs mt-1" data-testid="text-call-state">
              <span className={
                callState === "in-progress" ? "text-emerald-600 dark:text-emerald-400" :
                callState === "completed" ? "text-muted-foreground" :
                callState === "error" ? "text-red-600 dark:text-red-400" :
                "text-amber-600 dark:text-amber-400"
              }>
                {callState === "connecting" ? (phoneRouting ? "Ringing your phone…" : "Connecting…") :
                 callState === "ringing" ? (phoneRouting ? "Answer your phone to connect" : "Ringing…") :
                 callState === "in-progress" ? `In call · ${duration}s` :
                 callState === "completed" ? `Ended · ${duration}s` :
                 phoneRouting ? "Could not ring your phone" : "Browser dialer error"}
              </span>
            </div>
          )}
        </div>
        <Button size="icon" variant="ghost" onClick={onClose} className="h-7 w-7" data-testid="button-mini-dialer-close">
          <X className="h-4 w-4" />
        </Button>
      </div>

      {showConsentBanner && (
        <div className="rounded-md bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 p-2 text-xs flex items-start gap-2" data-testid="banner-two-party-consent">
          <AlertCircle className="h-4 w-4 mt-0.5 text-amber-700 dark:text-amber-400 shrink-0" />
          <span className="text-amber-900 dark:text-amber-200">
            You are calling a state that requires two-party consent. You must inform the other party that this call is being recorded.
          </span>
        </div>
      )}

      {tokenError ? (
        <div className="rounded-md bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 p-2 text-xs flex items-start gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 text-amber-700 dark:text-amber-400 shrink-0" />
          <div className="space-y-1">
            <div className="font-medium text-amber-900 dark:text-amber-200">Browser dialer unavailable</div>
            <div className="text-amber-900/80 dark:text-amber-200/80">
              Dial from your phone, then log the call below.
            </div>
            {tokenError && tokenError !== "true" && (
              // Shown inline rather than behind a disclosure triangle: this is
              // the only place the actual reason appears, and a collapsed
              // control is easy to miss (or to fail to open on touch).
              <div
                className="mt-1 font-mono text-[10px] text-amber-800 dark:text-amber-300 break-all whitespace-pre-wrap"
                data-testid="text-dialer-error-detail"
              >
                {tokenError}
              </div>
            )}
          </div>
        </div>
      ) : phoneRouting ? (
        // Phone-routed reps: no SDK leg, no in-browser hangup. We ring their
        // phone via the bridge and they manage the call on their handset.
        inCall ? (
          <div
            className="w-full flex items-center justify-center gap-2 rounded-md border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-sm text-amber-900 dark:text-amber-200"
            data-testid="status-mini-dialer-phone-bridge"
          >
            <Phone className="h-4 w-4" /> Answer your phone to connect
          </div>
        ) : (
          <Button
            className="w-full"
            onClick={startCall}
            data-testid="button-mini-dialer-call"
          >
            <Phone className="h-4 w-4 mr-2" /> Call now
          </Button>
        )
      ) : tokenProbe.isLoading || (!sdkReady && callState === "idle") ? (
        <div className="text-xs text-muted-foreground flex items-center gap-2">
          <Loader2 className="h-3 w-3 animate-spin" /> Connecting to dialer…
        </div>
      ) : inCall ? (
        <Button
          variant="destructive"
          className="w-full"
          onClick={hangup}
          data-testid="button-mini-dialer-hangup"
        >
          <PhoneOff className="h-4 w-4 mr-2" /> Hang up
        </Button>
      ) : (
        <Button
          className="w-full"
          onClick={startCall}
          disabled={!sdkReady}
          data-testid="button-mini-dialer-call"
        >
          <Phone className="h-4 w-4 mr-2" /> Call now
        </Button>
      )}

      {/* DTMF keypad — browser calls only, only while in-progress */}
      {callState === "in-progress" && !phoneRouting && (
        <div className="space-y-2">
          <Button
            variant="outline"
            size="sm"
            className="w-full h-8 text-xs"
            onClick={() => setShowKeypad(v => !v)}
            data-testid="button-mini-dialer-toggle-keypad"
          >
            <Keyboard className="h-3.5 w-3.5 mr-1.5" />
            {showKeypad ? "Hide keypad" : "Keypad"}
          </Button>
          {showKeypad && (
            <div className="space-y-1.5">
              <div
                className="text-center font-mono text-sm tracking-[0.25em] bg-muted/50 rounded px-2 py-1 min-h-[28px] text-foreground/80"
                data-testid="text-mini-dialer-dtmf-display"
              >
                {dialedDigits || <span className="text-muted-foreground/50 text-xs not-italic">dial digits…</span>}
              </div>
              <div className="grid grid-cols-3 gap-1.5">
                {(["1","2","3","4","5","6","7","8","9","*","0","#"] as const).map(digit => (
                  <Button
                    key={digit}
                    variant="outline"
                    size="sm"
                    className="h-10 text-base font-semibold tracking-tight select-none"
                    onClick={() => sendDtmf(digit)}
                    data-testid={`button-mini-dialer-dtmf-${digit === "*" ? "star" : digit === "#" ? "pound" : digit}`}
                  >
                    {digit}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {tokenError && (
        isMobile ? (
          <a
            href={`tel:${phone}`}
            className="block w-full text-center bg-primary text-primary-foreground rounded-md py-2 text-sm font-medium hover:bg-primary/90"
            data-testid="link-mini-dialer-tel"
          >
            <Phone className="inline h-4 w-4 mr-2" />
            Tap to call
          </a>
        ) : (
          <div className="flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2" data-testid="text-mini-dialer-phone-desktop">
            <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <span className="text-sm font-medium flex-1 select-all" data-testid="text-mini-dialer-phone-number">{phone}</span>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 flex-shrink-0"
              data-testid="button-mini-dialer-copy-number"
              onClick={() => {
                navigator.clipboard.writeText(phone).then(() => {
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                });
              }}
            >
              {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
            </Button>
          </div>
        )
      )}

      <div className="space-y-2 pt-1">
        {duration > 0 && (
          <div className="text-xs text-muted-foreground" data-testid="text-mini-dialer-duration">
            Duration: {duration}s
          </div>
        )}
        <div>
          <Label htmlFor="notes" className="text-xs">Notes</Label>
          <Textarea
            id="notes"
            ref={notesRef}
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="What did you discuss?"
            className="text-sm"
            data-testid="textarea-mini-dialer-notes"
          />
        </div>
        <Button
          className="w-full"
          size="sm"
          variant={inCall ? "outline" : "default"}
          onClick={() => logCall.mutate()}
          disabled={logCall.isPending}
          data-testid="button-mini-dialer-log"
        >
          {logCall.isPending ? <Loader2 className="h-3 w-3 mr-2 animate-spin" /> : null}
          Log call
        </Button>
      </div>
    </div>
  );
}
